import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Pagination from "react-js-pagination";
import { Formik } from "formik";
import { fetchApprovedSuppliersCategories } from "./ordersCrud";
import FormControl from "@material-ui/core/FormControl";
import { Select, FilledInput } from "@material-ui/core";
import MenuItem from "@material-ui/core/MenuItem";

import {SuppliersCard} from "./SuppliersCard";

// Custom styles for enhanced visual appearance
const cardStyles = `
  .supplier-card-wrapper {
    transition: all 0.3s ease;
  }
  
  .supplier-card-wrapper:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  }
  
  .supplier-card-wrapper .MuiCard-root {
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    transition: all 0.3s ease;
    height: 100%;
  }
  
  .supplier-card-wrapper:hover .MuiCard-root {
    box-shadow: 0 8px 20px rgba(0,0,0,0.15);
  }
  
  .supplier-card-wrapper .MuiCardActionArea-root {
    height: 100%;
  }
  
  .supplier-card-wrapper .MuiCardContent-root {
    padding: 16px;
  }
  
  .supplier-card-wrapper .MuiTypography-h5 {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 8px;
    color: #2c3e50;
  }
  
  .supplier-card-wrapper .MuiTypography-body2 {
    font-size: 0.875rem;
    color: #6c757d;
    line-height: 1.4;
  }
  
  .supplier-card-wrapper .MuiCardActions-root {
    padding: 12px 16px;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  }
  
  .supplier-card-wrapper .btn-success {
    border-radius: 8px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.3s ease;
  }
  
  .supplier-card-wrapper .btn-success:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(40, 167, 69, 0.3);
  }
  
  .pagination-container {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
  }
  
  .page-item.active .page-link {
    background-color: #007bff;
    border-color: #007bff;
  }
  
  .page-link {
    border-radius: 6px;
    margin: 0 2px;
    border: 1px solid #dee2e6;
    color: #495057;
    transition: all 0.2s ease;
  }
  
  .page-link:hover {
    background-color: #e9ecef;
    border-color: #adb5bd;
    color: #495057;
  }
`;

export function SuppliersList({setSuppliers, setIsSupplerSelected, handleSupplierChange, setIsSuppliersFetched}) {
    const {user, accessToken} = useSelector(
        ({auth}) => ({
            user: auth.user != null ? auth.user : null,
            accessToken: auth.authToken,
        })
    )

    const initialPaginationOptions = {
        totalSize: 0,
        sizePerPage: 9,
        page: 1
    }

    const [suppliers, setSuppliersLocal] = useState([]);
    const [paginationOptions, setPaginationOptions] = useState(initialPaginationOptions);
    const [searchName, setSearchName] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const fetchSuppliers = (pageNumber, pageSize, searchText, isFirstCall = false) => {
        setIsLoading(true);
        const queryParams = {
            page: pageNumber,
            sizePerPage: pageSize,
            name: searchText || ""
        };

        console.log("Getting suppliers with queryParams: ", queryParams)

        fetchApprovedSuppliersCategories(accessToken, queryParams).then(response => {
            console.log("Response: ", response)
            const suppliersData = response.data.data || response.data;
            const totalCount = response.data.totalCount || suppliersData.length;
            
            setSuppliersLocal(suppliersData);
            setPaginationOptions({
                page: pageNumber,
                totalSize: totalCount,
                sizePerPage: pageSize
            });
            setIsSuppliersFetched(true);
            setSuppliers(suppliersData);
            
            // Auto-select supplier if there's only one
            if (suppliersData.length === 1 && isFirstCall) {
                handleSupplierChange(suppliersData[0]);
            }
            
            setIsLoading(false);
        }).catch(err => {
            console.log("Error fetching suppliers: ", err);
            setIsLoading(false);
        });
    };

    useEffect(() => {
        fetchSuppliers(paginationOptions.page, paginationOptions.sizePerPage, searchName, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePageChange = (pageNumber) => {
        fetchSuppliers(pageNumber, paginationOptions.sizePerPage, searchName);
    };

    const handlePageSizeChange = (pageSize) => {
        fetchSuppliers(paginationOptions.page, pageSize, searchName);
    };

    const handleSearch = (searchText) => {
        setSearchName(searchText);
        fetchSuppliers(1, paginationOptions.sizePerPage, searchText);
    };

    const handleClearSearch = () => {
        setSearchName("");
        fetchSuppliers(1, paginationOptions.sizePerPage, "");
    };

    return (
        <>
            <style>{cardStyles}</style>
            
            <div className="row justify-content-between mb-4">
                <div className="col-lg-6">
                    <Formik
                        initialValues={{searchText: ""}}
                        onSubmit={(values) => {
                            handleSearch(values.searchText);
                        }}>
                        {({values, handleSubmit, handleBlur, handleChange, setFieldValue}) => (
                            <form onSubmit={handleSubmit} className="form form-label-right w-100">
                                <div className="form-group row">
                                    <div className="col-lg-8">
                                        <input
                                            type="text"
                                            className="form-control form-control-solid"
                                            name="searchText"
                                            placeholder="Search suppliers by name..."
                                            onBlur={handleBlur}
                                            value={values.searchText}
                                            onChange={(e) => {
                                                setFieldValue("searchText", e.target.value);
                                                handleSearch(e.target.value);
                                            }}
                                        />
                                        <small className="form-text text-muted">
                                            <b>Search</b> by supplier name
                                        </small>
                                    </div>
                                    <div className="col-lg-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFieldValue("searchText", '');
                                                handleClearSearch();
                                            }}
                                            className="btn btn-light-primary font-weight-bold px-6"
                                        >
                                            <i className="fas fa-times mr-2"></i>
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </form>
                        )}
                    </Formik>
                </div>
            </div>

            {isLoading ? (
                <div className="row justify-content-center mt-20">
                    <span className="ml-3 spinner spinner-lg spinner-primary"></span>
                </div>
            ) : (
                <>
                    
                    {suppliers && suppliers.length > 0 ? (
                        <>
                            <div className="card card-custom gutter-b">
                                <div className="card-header border-0 pt-5">
                                    <h3 className="card-title font-weight-bolder text-dark">
                                        <i className="fas fa-building text-primary mr-2"></i>
                                        Available Suppliers
                                    </h3>
                                </div>
                                <div className="card-body p-0">
                                    <div className="row g-4 p-6">
                                        {suppliers.map((supplier, index) => (
                                            <div key={index} className="col-xl-4 col-lg-6 col-md-6 col-sm-12">
                                                <div className="supplier-card-wrapper h-100">
                                                    <SuppliersCard 
                                                        className="card-stretch gutter-b h-100" 
                                                        supplier={supplier} 
                                                        setIsSupplerSelected={setIsSupplerSelected} 
                                                        handleSupplierChange={handleSupplierChange}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {paginationOptions.totalSize > paginationOptions.sizePerPage && (
                                        <div className="border-top">
                                            <div className="row justify-content-between align-items-center p-6">
                                                <div className="col-lg-3">
                                                    <div className="d-flex align-items-center">
                                                        <span className="text-muted mr-3">Items per page:</span>
                                                        <FormControl variant="filled" style={{ minWidth: "80px" }}>
                                                            <Select
                                                                MenuProps={{ autoFocus: false }}
                                                                value={paginationOptions.sizePerPage}
                                                                input={<FilledInput 
                                                                    name="pageSize" 
                                                                    id="pageSize"
                                                                    onChange={(element) => {
                                                                        handlePageSizeChange(element.target.value);
                                                                    }}
                                                                    className="form-control form-control-solid h-auto" 
                                                                />}
                                                            >
                                                                <MenuItem key={"pageSize9"} value={9}>9</MenuItem>
                                                                <MenuItem key={"pageSize18"} value={18}>18</MenuItem>
                                                                <MenuItem key={"pageSize27"} value={27}>27</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </div>
                                                </div>
                                                <div className="col-lg-6">
                                                    <div className="d-flex justify-content-center">
                                                        <Pagination
                                                            itemClass="page-item"
                                                            linkClass="page-link"
                                                            activePage={paginationOptions.page}
                                                            itemsCountPerPage={paginationOptions.sizePerPage}
                                                            totalItemsCount={paginationOptions.totalSize}
                                                            pageRangeDisplayed={5}
                                                            onChange={handlePageChange.bind(this)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-3">
                                                    <div className="text-right">
                                                        <span className="text-muted">
                                                            Showing {((paginationOptions.page - 1) * paginationOptions.sizePerPage) + 1} to {Math.min(paginationOptions.page * paginationOptions.sizePerPage, paginationOptions.totalSize)} of {paginationOptions.totalSize} suppliers
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="card card-custom gutter-b">
                            <div className="card-body p-0">
                                <div className="row justify-content-center px-8 py-md-27 px-md-0">
                                    <div className="col-md-10">
                                        <div className="col-sm bg-light-primary px-6 py-4 rounded-xl mr-7">
                                            <div className="text-center mb-4">
                                                <span className="card-label font-weight-bolder text-dark text-center font-size-lg">
                                                    {searchName ? "No suppliers found matching your search." : "No approved suppliers found."}
                                                </span>
                                            </div>
                                            <div className="text-center">
                                                <span className="text-dark-75 mt-3 font-weight-bold font-size-lg">
                                                    {searchName ? "Try adjusting your search criteria." : "To start making orders wait for an application to be approved."}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
}